import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { Invoice } from '../types/myaccount';
import api from '../services/api';
import InvoicePaymentReviewModal from './InvoicePaymentReviewModal';
import MyAccountCard from './MyAccountCard';
import MyAccountPageShell from './MyAccountPageShell';
import { LoadingBar, SummaryCardsSkeleton, TableSkeleton } from './MyAccountLoadingStates';
import { getStatusBadgeModifierClass } from '../utils/statusBadges';

interface ViewInvoiceProps {
    wekaPagePermissions?: Record<string, boolean>;
}

const ViewInvoice: React.FC<ViewInvoiceProps> = ({ wekaPagePermissions = {} }) => {
    const invoicePaymentEnabled = Boolean((window as any)?.wekaApiSettings?.features?.invoice_payment);
    const { invoiceId } = useParams<{ invoiceId: string }>();
    const [searchParams] = useSearchParams();
    const invoiceIdFromQuery = searchParams.get('invoice_id');
    
    const [invoice, setInvoice] = useState<Invoice | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [paying, setPaying] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    const canPayInvoice = wekaPagePermissions.pay_invoice !== false;
    const canDownloadInvoicePdf = wekaPagePermissions.download_invoice_pdf !== false;

    // Use either route param or query param
    const actualInvoiceId = invoiceId || invoiceIdFromQuery;

    useEffect(() => {
        const fetchInvoice = async () => {
            if (!actualInvoiceId) {
                setError('No invoice ID provided');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                
                // First try to get all invoices and find the matching one by invoice_number
                const invoices = await api.getInvoices();
                const foundInvoice = invoices.find(
                    inv => inv.invoice_number === actualInvoiceId || inv.id.toString() === actualInvoiceId
                );

                if (foundInvoice) {
                    setInvoice(foundInvoice);
                    
                    // Use the PDF URL from the API if available
                    if (foundInvoice.pdf_url) {
                        setPdfUrl(foundInvoice.pdf_url);
                        console.log('PDF URL from API:', foundInvoice.pdf_url);
                    } else if (foundInvoice.pdf) {
                        setPdfUrl(foundInvoice.pdf);
                        console.log('PDF URL from pdf field:', foundInvoice.pdf);
                    } else {
                        console.log('No PDF available for this invoice');
                        setPdfUrl(null);
                    }
                    
                    console.log('Invoice found:', foundInvoice);
                } else {
                    setError(`Invoice ${actualInvoiceId} not found in your account.`);
                }
            } catch (err) {
                console.error('Error fetching invoice:', err);
                setError('Failed to load invoice. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchInvoice();
    }, [actualInvoiceId]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount: string | number) => {
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(numAmount);
    };

    const getProductUrl = (productId?: number) => {
        const resolvedId = Number(productId || 0);
        if (!Number.isFinite(resolvedId) || resolvedId <= 0) {
            return '';
        }
        return `/?post_type=product&p=${resolvedId}`;
    };

    const normalizeInvoiceStatus = (rawStatus?: string) => {
        if (!rawStatus) {
            return 'Unknown';
        }

        return rawStatus
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const getInvoiceStatusBadgeClass = (rawStatus?: string) => getStatusBadgeModifierClass('invoices', rawStatus);

    const handlePayInvoice = async () => {
        if (!invoice) return;
        if (!invoicePaymentEnabled || !canPayInvoice) return;
        
        const balance = parseFloat(invoice.balance);
        if (balance <= 0) {
            toast.error('This invoice is already paid.');
            return;
        }

        setReviewOpen(true);
    };

    const handleCreatePaymentOrder = async () => {
        if (!canPayInvoice) {
            toast.error('Paying invoices is disabled for this page.');
            return;
        }

        if (!invoice || paying) {
            return;
        }

        try {
            setPaying(true);
            
            const response = await api.createInvoicePayment([invoice.invoice_number]);
            window.location.href = response.order_pay_url || response.order_view_url || '/my-account/orders';
        } catch (err) {
            console.error('Payment error:', err);
            toast.error(err instanceof Error ? err.message : 'An error occurred while processing your payment.');
            setPaying(false);
        }
    };

    if (loading) {
        return (
            <div className="weka-my-account-page weka-my-account-page--detail">
                <MyAccountCard
                    variant="page"
                    slug="view-invoice-loading"
                    className="view-order-card view-invoice-card"
                    innerClassName="view-order-body"
                    wrapContent={false}
                >
                        <div className="view-order-header view-invoice-header">
                            <div>
                                <LoadingBar height="18px" width="140px" style={{ marginBottom: '16px' }} />
                                <LoadingBar height="28px" width="30%" style={{ marginBottom: '10px' }} />
                                <LoadingBar height="16px" width="34%" />
                            </div>
                            <LoadingBar height="32px" width="112px" style={{ borderRadius: '999px' }} />
                        </div>
                        <div className="view-order-summary-grid">
                            <SummaryCardsSkeleton count={5} minHeight="88px" />
                        </div>
                        <div className="view-order-section">
                            <LoadingBar height="20px" width="14%" style={{ marginBottom: '16px' }} />
                            <div className="d-flex gap-2 flex-wrap">
                                <LoadingBar height="38px" width="180px" style={{ borderRadius: '8px' }} />
                                <LoadingBar height="38px" width="124px" style={{ borderRadius: '8px' }} />
                            </div>
                        </div>
                        <div className="view-order-section">
                            <LoadingBar height="20px" width="12%" style={{ marginBottom: '16px' }} />
                            <TableSkeleton columns={5} rows={6} />
                        </div>
                        <div className="view-order-section view-order-totals-section">
                            <div className="skeleton" style={{ height: '180px', borderRadius: '10px' }} />
                        </div>
                </MyAccountCard>
            </div>
        );
    }

    if (error) {
        return (
            <MyAccountPageShell
                title="Invoice Details"
                subtitle="Review invoice status, line items, and payment actions."
            >
                    <div className="alert alert-danger" role="alert">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                    </div>
                    <Link to="/invoices" className="button">
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to Invoices
                    </Link>
            </MyAccountPageShell>
        );
    }

    if (!invoice) {
        return (
            <MyAccountPageShell
                title="Invoice Details"
                subtitle="Review invoice status, line items, and payment actions."
            >
                    <div className="alert alert-warning" role="alert">
                        Invoice not found
                    </div>
                    <Link to="/invoices" className="button">
                        Back to Invoices
                    </Link>
            </MyAccountPageShell>
        );
    }

    const invoiceNumber = invoice.invoice_number || String(invoice.id);
    const totalAmount = Number.parseFloat(invoice.total || '0') || 0;
    const taxAmount = Number.parseFloat(invoice.tax || '0') || 0;
    const discountAmount = Number.parseFloat(invoice.discount || '0') || 0;
    const balanceAmount = Number.parseFloat(invoice.balance || '0') || 0;
    const subtotalAmount = totalAmount - taxAmount + discountAmount;
    const lineItems = Array.isArray(invoice.line_items) ? invoice.line_items : [];
    const hasBalanceDue = balanceAmount > 0;

    return (
        <div className="weka-my-account-page weka-my-account-page--detail">
            <MyAccountCard
                variant="page"
                slug="view-invoice"
                className="view-order-card view-invoice-card"
                innerClassName="view-order-body"
                wrapContent={false}
            >
                <div className="view-order-header view-invoice-header">
                    <div>
                        <Link to="/invoices" className="view-order-back-link weka-account-link">
                            <i className="fas fa-arrow-left" aria-hidden="true"></i>
                            Back to Invoices
                        </Link>
                        <h2 className="view-order-title">
                            Invoice #{invoiceNumber}
                        </h2>
                        <p className="view-order-subtitle">
                            Issued on {formatDate(invoice.date)}
                            {invoice.due_date ? ` • Due ${formatDate(invoice.due_date)}` : ''}
                        </p>
                    </div>
                    <span className={`view-order-status ${getInvoiceStatusBadgeClass(invoice.status)}`}>
                        {normalizeInvoiceStatus(invoice.status)}
                    </span>
                </div>

                <div className="view-order-summary-grid">
                    <div className="view-order-summary-card">
                        <span className="label">Invoice Number</span>
                        <strong>#{invoiceNumber}</strong>
                    </div>
                    <div className="view-order-summary-card">
                        <span className="label">Invoice Date</span>
                        <strong>{formatDate(invoice.date)}</strong>
                    </div>
                    {invoice.due_date && (
                        <div className="view-order-summary-card">
                            <span className="label">Due Date</span>
                            <strong>{formatDate(invoice.due_date)}</strong>
                        </div>
                    )}
                    <div className="view-order-summary-card">
                        <span className="label">Total Amount</span>
                        <strong>{formatCurrency(totalAmount)}</strong>
                    </div>
                    <div className="view-order-summary-card">
                        <span className="label">Balance Due</span>
                        <strong>{formatCurrency(balanceAmount)}</strong>
                    </div>
                </div>

                {(!canPayInvoice || !canDownloadInvoicePdf) && (
                    <div className="alert alert-info" role="status">
                        {[
                            !canPayInvoice ? 'Paying invoices is disabled.' : '',
                            !canDownloadInvoicePdf ? 'Downloading invoice PDFs is disabled.' : '',
                        ].filter(Boolean).join(' ')}
                    </div>
                )}

                {(invoicePaymentEnabled && canPayInvoice && hasBalanceDue) || (canDownloadInvoicePdf && pdfUrl) ? (
                    <div className="view-order-section">
                        <h3>Actions</h3>
                        <div className="view-order-actions">
                            {invoicePaymentEnabled && canPayInvoice && hasBalanceDue && (
                                <button
                                    onClick={handlePayInvoice}
                                    disabled={paying}
                                    className="btn btn-success"
                                >
                                    <i className="fas fa-credit-card me-2"></i>
                                    {paying ? 'Processing...' : `Pay Invoice (${formatCurrency(balanceAmount)})`}
                                </button>
                            )}
                            {canDownloadInvoicePdf && pdfUrl && (
                                <a
                                    href={pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="button"
                                >
                                    <i className="fas fa-file-pdf me-2"></i>
                                    View PDF
                                </a>
                            )}
                        </div>
                    </div>
                ) : null}

                {invoice.order_id && (
                    <div className="view-order-section">
                        <h3>Related Order</h3>
                        <div className="view-order-actions">
                            <Link
                                to={`/view-order/${invoice.order_id}`}
                                className="button weka-secondary-btn"
                            >
                                View Order #{invoice.order_id}
                            </Link>
                        </div>
                    </div>
                )}

                <div className="view-order-section">
                    <h3>Items</h3>
                    {lineItems.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table view-order-items-table">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Qty</th>
                                        <th className="text-end">Unit Price</th>
                                        <th className="text-end">Discount</th>
                                        <th className="text-end">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lineItems.map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td>
                                                <div className="view-order-item-name">
                                                    {item.product_id && item.product_id > 0 ? (
                                                        <a href={getProductUrl(item.product_id)} className="weka-account-link">
                                                            {item.title}
                                                        </a>
                                                    ) : (
                                                        item.title
                                                    )}
                                                </div>
                                                {item.sku && item.sku.trim() !== '' && (
                                                    <ul className="view-order-item-meta">
                                                        <li>
                                                            <span>SKU:</span> {item.sku}
                                                        </li>
                                                    </ul>
                                                )}
                                            </td>
                                            <td>{item.quantity}</td>
                                            <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                            <td className="text-end">
                                                {parseFloat(item.discount) > 0 ? formatCurrency(item.discount) : '-'}
                                            </td>
                                            <td className="text-end fw-bold">{formatCurrency(item.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="mb-0 text-muted">No line items available for this invoice.</p>
                    )}
                </div>

                <div className="view-order-section view-order-totals-section">
                    <h3>Totals</h3>
                    <dl>
                        <div>
                            <dt>Subtotal</dt>
                            <dd>{formatCurrency(subtotalAmount)}</dd>
                        </div>
                        {discountAmount > 0 && (
                            <div>
                                <dt>Discount</dt>
                                <dd>-{formatCurrency(discountAmount)}</dd>
                            </div>
                        )}
                        {taxAmount > 0 && (
                            <div>
                                <dt>Tax</dt>
                                <dd>{formatCurrency(taxAmount)}</dd>
                            </div>
                        )}
                        <div className="is-total">
                            <dt>Total</dt>
                            <dd>{formatCurrency(totalAmount)}</dd>
                        </div>
                        {hasBalanceDue && (
                            <div>
                                <dt>Balance Due</dt>
                                <dd>{formatCurrency(balanceAmount)}</dd>
                            </div>
                        )}
                    </dl>
                </div>
            </MyAccountCard>

            <InvoicePaymentReviewModal
                isOpen={reviewOpen}
                items={[{
                    invoice_number: invoice.invoice_number,
                    balance: formatCurrency(balanceAmount),
                    date: formatDate(invoice.date),
                    due_date: invoice.due_date ? formatDate(invoice.due_date) : undefined,
                }]}
                totalLabel={formatCurrency(balanceAmount)}
                processing={paying}
                onClose={() => {
                    if (!paying) {
                        setReviewOpen(false);
                    }
                }}
                onConfirm={handleCreatePaymentOrder}
            />
        </div>
    );
};

export default ViewInvoice;
